import _ from 'lodash';
import {
  List,
  OrderedMap,
  Map,
  OrderedSet,
  fromJS,
  type Iterable as ImmutableIterable,
  Set as ImmSet,
} from 'immutable';
import {reportBug, reportBugOnce} from '@packages/utilities/reporter';
import {COMBINATORS} from '@packages/systems/core/constants/Selectors';
// Not the barrel: it cycles back through this file at module scope.
// eslint-disable-next-line webflow/module-top-level-imports
import {isLibraryTagStyle} from '@packages/systems/libraries/tagStyles';
import {importLess} from './StyleLessUtils';
import {DEFAULT_TAG_STYLES} from '../constants/DefaultTagStyles';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {
  sortVariants,
  parseVariantKey,
  createVariantKey,
} from '@packages/systems/style-legacy/variants';
import {
  cssToPropertyMap,
  cssTextToPropertyValue,
} from '@packages/systems/core/utils/CssStringUtils';
import {StyleBlockRecord} from '@packages/systems/style-legacy/records';
import {
  getOrderedBreakpointIds,
  LARGER_BREAKPOINTS,
  keyByBreakpoints,
} from '@packages/systems/style/media-queries';
import {
  buildSelectorToken,
  getSelectorText,
  getSelectorScore,
  toClassName,
  isValidClassName,
  getUserlandPrefix,
  VariantDataMap,
  VariantStateKey,
  emptyVariantDataMap,
} from '@packages/systems/style-legacy/selectors';
import {getTokensForVariants, createStyleRuleGuid} from './StyleRuleUtils';

import {
  extractBool,
  equals,
  allPass,
  emptyObject,
  emptyArray,
} from '@packages/utilities/fp/utils';
import {assoc, lookup} from '@packages/utilities/key-map';
import {maybe, None, Option} from '@packages/utilities/fp/option';
import {find, lookup as immLookup} from '@packages/systems/core/utils/ImmUtils';
import {
  getPrefix,
  createConflictResolver,
} from '@packages/utilities/string-utils';
import {incrementGuid} from '@packages/utilities/guid';

import type {
  RawStyleBlockServerType as ServerStyleBlock,
  RawStyleBlockServerDataType,
  PseudoStateKey,
  CustomNodeStateKey,
} from '@packages/systems/core/utils/StyleTypes';
import type {
  BreakpointID,
  CssPropertyMap,
  StyleBlockId,
} from '@packages/systems/style/types';
import type {
  Selector,
  SelectorChunk,
} from '@packages/systems/style-legacy/rules';

import type {
  StyleBlock,
  StyleBlockVariant,
} from '@packages/systems/style-legacy/records';
import type {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import type {
  ChangedRuleType,
  DecoratableWithMaps,
  StyleBlockStateShape,
  StyleBlockStateType,
} from '../states/StyleBlockStateTypes';
import {COMPONENT_AFFECTS_PREFIX} from '../states/StyleBlockStateTypes';
import {
  QualifiedMap,
  MutableQualifiedMap,
  PluginName,
  namesEqual,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {Plugins} from '@packages/systems/wfdl';
import {
  ClassNameMap,
  ClassNameAndVariantStateKeys,
} from '@packages/systems/style/types';
import * as KeySet from '@packages/utilities/key-set';
import {type AffectedNodesByStyleBlockId} from '@packages/shared/utils/AffectedNodesUtils';
import {getElementStyleClassName} from './ElementStyleUtils';

export type ImportStrategyType = 'DEFAULT' | 'OVERRIDE' | 'KEEP' | 'COPY';

const bgImgRegex = /@img_[a-zA-Z0-9]+\s*(?=,|;|$)/g;

/**
 * Returns all of the ids with the format `@img_${assetId}`
 * @param  {String}   styleLess         The styleLess from a @type {StyleBlock} that you want to extract the image ids from
 * @return {String[]} All of the image ids included in the styleless
 */
export const extractStyleLessBackgroundImageIds = (
  styleLess: string
): string[] =>
  styleLess
    .match(bgImgRegex)
    ?.map((imageAssetId: string) => imageAssetId.substr(5)) || [];

const replaceStyleLessBackgroundImages = weakMemo(
  (assetMap: Map<string, string>) => (styleLess: string) =>
    styleLess.replace(bgImgRegex, (imgId) => {
      const id = imgId.substr(5);
      return '@img_' + assetMap.get(id, id);
    })
);

const replaceBackgroundImagesInVariants = weakMemo(
  (assetMap: Map<string, string>) =>
    (variants: OrderedMap<BreakpointID, StyleBlockVariant>) =>
      variants.map((variant) =>
        variant.update('styleLess', replaceStyleLessBackgroundImages(assetMap))
      )
);

export const replaceBackgroundImages =
  (assetMap: Map<string, string>) =>
  (styleBlock: StyleBlock): StyleBlock =>
    styleBlock
      .update('styleLess', replaceStyleLessBackgroundImages(assetMap))
      .update('variants', replaceBackgroundImagesInVariants(assetMap));

/**
 * Creates a new StyleBlock object.
 * @param  {String}   options._id         A GUID for this block.
 * @param  {Boolean}  options.fake        If true, is marked as a transient block until it's created by the user.
 * @param  {String}   options.type        The type of selector - only "tag" or "class" are supported.
 * @param  {String}   options.name        The name of the selector. e.g. "h1" for tags, or "Hero Section" for classes.
 * @param  {String}   options.comb        The block combinator in reference to the parent block (if any).
 * @param  {String}   options.styleLess   Style information for the main rule.
 * @param  {Object}   options.variants    A map of variations for pseudo states and cross-media-query style changes from the main rule.
 * @param  {String[]} options.children    An array of children GUIDs, if this StyleBlock has composed children.
 * @param  {String}   options.createdBy   The userId of the creator of the StyleBlock.
 * @return {StyleBlockRecord}
 */
export function buildStyleBlock({
  _id,
  fake = false,
  type,
  name,
  namespace = '',
  comb = '',
  styleLess = '',
  variants = {},
  children = [],
  pluginType,
  createdBy,
  origin,
}: RawStyleBlockType): StyleBlock {
  if (!_id || !_.isString(_id) || _id.trim() === '') {
    throw new Error('Invalid style guid: ' + _id);
  }
  if (!type || !(type === 'tag' || type === 'class' || type === 'element')) {
    throw new Error('Invalid style type: ' + type);
  }
  if (!name || !_.isString(name) || name.trim() === '') {
    throw new Error('Invalid style name: ' + name);
  }

  return StyleBlockRecord({
    _id,
    fake,
    type,
    name,
    namespace,
    comb,
    styleLess,
    variants: fromJS(variants, (key, value) => {
      return key === 'styleLess' ? value : value.toOrderedMap();
    }) as any,
    children: OrderedSet(children),
    pluginType,
    createdBy,
    origin: origin ?? null,
  });
}

/**
 * Converts the server-side MongoDB record to a StyleBlockRecord.
 * @param  {Object}           raw                 The server-side StyleBlock from remote siteData, e.g. { _id: 'bla', data: { ... } }
 * @param  {AssetState}       options.assetState  The Flux AssetStore state of all images for the site being imported.
 * @param  {Object[]}         options.errors      An array to push import errors to, so that we can log them for debugging.
 * @return {StyleBlockRecord}
 */
export function importStyleBlock(
  raw: ServerStyleBlock,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ assetState: any; errors: { [key: string]: { sel?: string | undefined; text?: string | undefined; less?: string | undefined; }; }[]; }': assetState, errors
  {
    assetState = null,
    errors = [],
  }: {
    assetState: any;
    errors: {
      [key: string]: {
        sel?: string;
        text?: string;
        less?: string;
      };
    }[];
  } = {}
): StyleBlock {
  const data = raw.data || emptyObject;
  const importLessOptions = {assetState, errors} as const;

  const dataVariants = data.variants;
  let cleanVariants;

  if (dataVariants) {
    cleanVariants = {};

    // Filter variant data so that we only get a styleLess property for each one
    for (const key in dataVariants) {
      const variant = dataVariants[key];
      // @ts-expect-error - TS18048 - 'variant' is possibly 'undefined'. | TS18048 - 'variant' is possibly 'undefined'.
      if (!variant.styleLess && !variant.styleText) continue;
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      cleanVariants[key] = {styleLess: importLess(variant, importLessOptions)};
    }
  } else {
    cleanVariants = emptyObject;
  }

  let blockName = data.name;

  if (!isValidClassName(blockName)) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('InvalidClassName', 'While importing style block', {
      blockData: data,
      team: 'platform',
    });
    blockName = `-Error-${raw._id}`;
  }

  return buildStyleBlock({
    _id: raw._id,
    fake: raw.fake || false,
    type: data.type,
    name: blockName,
    namespace: data.namespace || '',
    comb: data.comb || '',

    styleLess: importLess(data, importLessOptions),
    variants: cleanVariants,
    children: data.children || emptyArray,
    createdBy: raw.data.createdBy,
    origin: raw.data.origin ?? null,
  });
}

function _isGlobalTagSelector(block: {
  data: RawStyleBlockServerDataType;
}): boolean | null | undefined {
  return (
    block &&
    block.data &&
    block.data.type === 'tag' &&
    (block.data.comb === COMBINATORS.NONE || !block.data.comb)
  );
}

export function createDefaultTagServerStyleBlock(
  defaultTag: (typeof DEFAULT_TAG_STYLES)[number]
): ServerStyleBlock {
  return {
    _id: createDefaultTagStyleBlockId(defaultTag.tag),
    fake: true,
    data: {
      type: 'tag',
      name: defaultTag.tag,
      namespace: null,
      sel: '',
      comb: '',
      styleLess: defaultTag.less,
      styleText: defaultTag.less,
      variants: {},
      children: [],
    },
  };
}

/**
 * Subroutine of `mergeDefaultStyleBlocks` that ensures that the given array of
 * raw server style blocks includes a body tag style block with the correct
 * `default-body` ID.
 */
function ensureDefaultBodyBlockId(rawBlocks: ServerStyleBlock[]): {
  rawBlocks: ServerStyleBlock[];
  renamedBodyTagId: string | null;
} {
  // If we already have a body tag style block with the correct `default-body`,
  // there's nothing to do:
  const defaultBodyTag = rawBlocks.find(
    (block) => block._id === 'default-body'
  );
  if (defaultBodyTag) {
    return {rawBlocks, renamedBodyTagId: null};
  }

  // Otherwise, if we have a site-level (non-library) `tag` style block without
  // the correct `default-body` ID, then modify its ID to be the correct one:
  const bodyTagIndex = rawBlocks.findIndex(
    (block) =>
      block.data.type === 'tag' &&
      block.data.name === 'body' &&
      block.data.origin == null
  );
  const bodyTag = rawBlocks[bodyTagIndex];
  if (bodyTagIndex >= 0 && bodyTag != null) {
    const originalBodyTagId = bodyTag._id;
    const newRawBlocks = rawBlocks.map((block, index) => {
      if (index === bodyTagIndex) {
        return {...block, _id: 'default-body'};
      }
      return block;
    });
    return {
      rawBlocks: newRawBlocks,
      renamedBodyTagId: originalBodyTagId,
    };
  }

  // Otherwise, if we don't have a body tag style block at all, we assume that
  // one will be added as part of `mergeDefaultStyleBlocks`
  return {rawBlocks, renamedBodyTagId: null};
}

/**
 * Applies a `default-body` rename (as reported by `mergeDefaultStyleBlocks`) to
 * a style-block id allow-list. `preProcessRawStyles` partitions the raw blocks
 * into two allow-lists — `styleBlockIds` (current styles) and
 * `baseStyleBlockIds` (branch base styles) — so the renamed id must be replaced
 * in whichever set actually contained it. Returns the set unchanged when it did
 * not contain the original id (or when nothing was renamed).
 */
function applyDefaultBodyRename(
  styleBlockIds: ImmSet<string>,
  renamedBodyTagId: string | null
): ImmSet<string> {
  if (renamedBodyTagId == null || !styleBlockIds.has(renamedBodyTagId)) {
    return styleBlockIds;
  }
  return styleBlockIds.remove(renamedBodyTagId).add('default-body');
}

/**
 * Merges in default (fake) style block into server-side raw blocks data if they weren't there, properly sorted.
 * Does not update membership sets; callers that track them should use
 * `mergeDefaultStyleBlocksWithMembership` instead.
 * @param  {Object[]}   rawBlocks               An array of style block data from the server.
 * @return {Object[]}                           An array including the server style blocks merged with the default fake ones.
 */
export function mergeDefaultStyleBlocks(rawBlocks: ServerStyleBlock[]): {
  rawBlocks: ServerStyleBlock[];
  renamedBodyTagId: string | null;
} {
  const ensureDefaultBodyBlockIdResult = ensureDefaultBodyBlockId(rawBlocks);
  rawBlocks = ensureDefaultBodyBlockIdResult.rawBlocks;
  const {renamedBodyTagId} = ensureDefaultBodyBlockIdResult;

  // Pull out already-defined real StyleBlocks for global tag selectors
  const globalTagSelectors = rawBlocks.filter(_isGlobalTagSelector);

  // Figure out which ones need to be added as fake=true blocks and add them
  const alreadyDefinedTags = globalTagSelectors
    // Exclude tag style blocks provided by library installations
    .filter((b) => !b.data.origin)
    .map((block) => block.data.name);
  const missingDefaultBlocks = DEFAULT_TAG_STYLES.filter(({tag}) => {
    return alreadyDefinedTags.indexOf(tag) < 0;
  });

  missingDefaultBlocks.forEach((defaultTag) => {
    globalTagSelectors.push(createDefaultTagServerStyleBlock(defaultTag));
  });

  // Make sure the order of global tag selectors matches the default order
  const tagOrder = DEFAULT_TAG_STYLES.map((style) => style.tag);
  globalTagSelectors.sort((lhs, rhs) => {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"a" | "blockquote" | "body" | "em" | "figcaption" | "figure" | "form" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "img" | "label" | "li" | "ol" | "p" | "strong" | "sub" | "sup" | "ul"'. | TS2345 - Argument of type 'string' is not assignable to parameter of type '"a" | "blockquote" | "body" | "em" | "figcaption" | "figure" | "form" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "img" | "label" | "li" | "ol" | "p" | "strong" | "sub" | "sup" | "ul"'.
    return tagOrder.indexOf(lhs.data.name) - tagOrder.indexOf(rhs.data.name);
  });

  // Once sorted, re-combine the tag selectors (which always go after)
  const nonGlobalTagSelectors = rawBlocks.filter((block) => {
    return !_isGlobalTagSelector(block);
  });

  return {
    rawBlocks: globalTagSelectors.concat(nonGlobalTagSelectors),
    renamedBodyTagId,
  };
}

/**
 * Convenience wrapper for callers of `mergeDefaultStyleBlocks` that also
 * maintain style-block membership sets (as captured by `preProcessRawStyles`).
 * `mergeDefaultStyleBlocks` may rename a legacy body tag block to the
 * canonical `default-body` id, so the reported rename is applied to both
 * membership sets. A set without the original id stays unchanged. Persistence
 * exports filter on these sets, so a missing ID silently drops that block from
 * export. When both partitions contain the same legacy GUID, only the first
 * (current) block is renamed, but both sets point to it; MPS base export then
 * uses current CSS and excludes the unrenamed base block. This limitation is
 * tracked in EXT-2842.
 * Callers without membership sets (e.g. base-content merge) should call
 * `mergeDefaultStyleBlocks` directly.
 */
export function mergeDefaultStyleBlocksWithMembership(
  rawBlocks: ServerStyleBlock[],
  styleBlockIds: ImmSet<string>,
  baseStyleBlockIds: ImmSet<string>
): {
  rawBlocks: ServerStyleBlock[];
  styleBlockIds: ImmSet<string>;
  baseStyleBlockIds: ImmSet<string>;
} {
  const {rawBlocks: mergedRawBlocks, renamedBodyTagId} =
    mergeDefaultStyleBlocks(rawBlocks);
  return {
    rawBlocks: mergedRawBlocks,
    styleBlockIds: applyDefaultBodyRename(styleBlockIds, renamedBodyTagId),
    baseStyleBlockIds: applyDefaultBodyRename(
      baseStyleBlockIds,
      renamedBodyTagId
    ),
  };
}

/**
 * Imports multiple server-side style blocks.
 * @param  {Object[]}   rawBlocks               An array of style block data from the server.
 * @param  {Boolean}    options.cleanChildIds   If true, will make sure that children arrays are filtered to only contain valid block guids.
 * @param  {AssetState} options.assetState      The Flux AssetStore state of image assets for the site being imported.
 * @param  {Object[]}   options.importErrors    A reference to an array to which import errors should be logged (for debugging).
 * @return {List}                               An Immutable.List of StyleBlockRecord objects.
 */
export function importStyleBlocks(
  rawBlocks: ServerStyleBlock[],
  // @ts-expect-error - TS2741 - Property 'cleanChildIds' is missing in type '{}' but required in type '{ cleanChildIds: boolean; assetState?: any; importErrors?: { [key: string]: { sel?: string | undefined; text?: string | undefined; less?: string | undefined; }; }[] | undefined; }'.
  {
    cleanChildIds = false,
    assetState = null,
    importErrors = [],
  }: {
    cleanChildIds: boolean;
    assetState?: any;
    importErrors?: {
      [key: string]: {
        sel?: string;
        text?: string;
        less?: string;
      };
    }[];
  } = {}
): List<StyleBlock> {
  const processedGuids = new Set();
  const duplicateBlocks: Array<
    | any
    | {
        guid: string;
        name: string;
        sel: undefined | string;
      }
  > = [];
  const validGuidMap = new Set(
    cleanChildIds ? rawBlocks.map((b) => b._id) : emptyArray
  );
  const list = List().asMutable();

  const rawBlocksLength = rawBlocks.length;

  for (let i = 0; i < rawBlocksLength; i++) {
    const rawBlock = rawBlocks[i];
    // If we encounter a StyleBlock with the same exact _id as a previous one, don't try to import it
    // This only affects pre-styles-next legacy sites
    // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
    if (!processedGuids.has(rawBlock._id)) {
      // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
      processedGuids.add(rawBlock._id);
    } else {
      duplicateBlocks.push({
        // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
        guid: rawBlock._id,
        // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
        name: rawBlock.data.name,
        // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
        sel: rawBlock.data.sel,
      });
      continue;
    }

    // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'. | TS18048 - 'rawBlock' is possibly 'undefined'.
    rawBlock.data.children = rawBlock.data.children || [];
    // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
    const childrenLength = rawBlock.data.children.length;

    // If there's a global list of valid block GUIDs, filter out invalid IDs in the children collection
    if (cleanChildIds && childrenLength > 0) {
      const children: Array<string> = [];

      for (let j = 0; j < childrenLength; j++) {
        // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
        const childGuid = rawBlock.data.children[j];
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        if (validGuidMap.has(childGuid)) children.push(childGuid);
      }

      // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and 'string[]' have no overlap.
      if (children.length !== rawBlock.data.children) {
        // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
        rawBlock.data.children = children;
      }
    }

    // @ts-expect-error - TS2345 - Argument of type 'RawStyleBlockServerType | undefined' is not assignable to parameter of type 'RawStyleBlockServerType'.
    list.push(importStyleBlock(rawBlock, {assetState, errors: importErrors}));
  }

  // @ts-expect-error - TS2322 - Type 'List<unknown>' is not assignable to type 'List<StyleBlock>'.
  return list.asImmutable();
}

/**
 * Collects block.data.affects data from the raw server-side blocks and builds a map for quick lookup.
 * @param  {Object[]}       rawBlocks             The raw style blocks from the server.
 * @param  {String[]}       options.validPageIds  The list of valid mongo page GUIDs. If omitted, all affects entries will be kept.
 * @return {Immutable.Map}
 */
export function buildAffectsMap(
  rawBlocks: ServerStyleBlock[],
  // @ts-expect-error - TS2741 - Property 'validPageIds' is missing in type '{}' but required in type '{ validPageIds: string[]; }'.
  {
    validPageIds = [],
  }: {
    validPageIds: Array<string>;
  } = {}
) {
  const map = Map<string, Map<string, number>>().asMutable();
  const rawBlocksLength = rawBlocks.length;

  for (let i = 0; i < rawBlocksLength; i++) {
    const rawBlock = rawBlocks[i];
    // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
    const data = rawBlock.data;
    const affectsMap = data && data.affects ? data.affects : null;

    if (affectsMap == null) {
      // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
      map.set(rawBlock._id, Map());
      continue;
    }

    if (validPageIds.length === 0) {
      // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
      map.set(rawBlock._id, Map(affectsMap));
      continue;
    }

    const newAffectsMap = Map().asMutable();
    const validPageIdsLength = validPageIds.length;

    // If requested, filter the affected page map to remove non-existent pages
    for (let j = 0; j < validPageIdsLength; j++) {
      const id = validPageIds[j];
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'PropertyKey'. | TS2538 - Type 'undefined' cannot be used as an index type.
      if (Object.hasOwn(affectsMap, id)) newAffectsMap.set(id, affectsMap[id]);
    }

    // Preserve component-level affects entries (format "component:<id>")
    // since they aren't page IDs and shouldn't be filtered out.
    for (const key of Object.keys(affectsMap)) {
      if (key.startsWith(COMPONENT_AFFECTS_PREFIX))
        newAffectsMap.set(key, affectsMap[key]);
    }

    // @ts-expect-error - TS18048 - 'rawBlock' is possibly 'undefined'.
    map.set(rawBlock._id, newAffectsMap.asImmutable());
  }

  return map.asImmutable();
}

/**
 * Creates a map that can be used to look up the parent StyleBlockRecord by guid.
 * @param  {Immutable.List<StyleBlockRecord>} styleBlocks  The pure JS list of StyleBlockRecord items in original server order.
 * @return {Immutable.Map}                                 A child_guid -> parent_guid map.
 */
export function buildParentIndex(styleBlocks: List<StyleBlock>) {
  const index: Record<string, any> = {};

  styleBlocks.forEach((styleBlockRecord) => {
    const parentGuid = styleBlockRecord.get('_id');

    styleBlockRecord.get('children').forEach((childGuid) => {
      index[childGuid] = parentGuid;
    });
  });

  return Map(index);
}

/**
 * Returns a map that contains keys for every CSS property that appears in the style block main or variant styles.
 * @param  {StyleBlockRecord} styleBlock      The StyleBlockRecord that we want to get the CSS properties from
 * @return {Immutable.Map<string, Map<string, boolean | string>>}   A CSSProperty -> Boolean Immutable.Map.
 */
export function getJustCssProperties(
  styleBlock: StyleBlock
): OrderedMap<BreakpointID, OrderedMap<string, boolean | string>> {
  const variants = sortVariants(styleBlock.get('variants', OrderedMap()));
  const mainStyles = styleBlock.get('styleLess');
  const mainPropertyMap: OrderedMap<string, boolean | string> = OrderedMap(
    cssToPropertyMap(mainStyles, true)
  );

  return variants.reduce(
    (result, variant, variantKey) => {
      /**
       * The cssPropertyMap for each variant, without any merged upstream values.
       *
       * E.g. if our style block looks like this:
       *
       *     {main: 'color: red;', medium: 'background: blue;'}
       *
       * ...`unmergedCssPropertyMap` looks like this:
       *
       *     Map({
       *       main: {color: true},
       *       medium: {background: true},
       *     })
       *
       * Note: This **only** includes mediaQueries we've already encountered
       * already in this reducer; this is safe because `variants` is an OrderedMap
       * sorted by mediaQuery.
       */
      const unmergedCssPropertyMap = result.set(
        variantKey,
        OrderedMap(cssToPropertyMap(variant.get('styleLess') || '', true))
      );

      // Tracking: https://webflow.atlassian.net/browse/STYL-792
      //
      // We must determine if we need to handle `variantState` (and perhaps the
      // long-existing `customState`) here based on usage.
      //
      // This function is used by `updateStyleRule` to compute the new
      // `StyleBlockState::cssPropertyMap`.
      //
      // It's strange that we don't already look at other pieces of the variant
      // key like `customState`; it's possible this is already leading to bugs,
      // but exactly how those bugs manifest is unclear, and there may be a
      // _good_ reason not to include `customState` (and thus perhaps
      // `variantState`) in our calculations.
      const {mediaQuery, pseudoState, variantState} =
        parseVariantKey(variantKey);
      const index = getOrderedBreakpointIds().indexOf(mediaQuery);
      const queries = getOrderedBreakpointIds().slice(0, index + 1);

      /**
       * The **merged** property map for the current variantKey (before merging in
       * pseudoStates)
       *
       * E.g. if our style block looks like this:
       *
       *     {main: 'color: red;', medium: 'background: blue;'}
       *
       * ...and our `variantKey` is `main`, then `baseMergedPropertyMap` looks
       * like this:
       *
       *     Map({
       *       color: true,
       *     })
       *
       * ...and our `variantKey` is `medium`, then `baseMergedPropertyMap` looks
       * like this:
       *
       *     Map({
       *       color: true,
       *       background: true,
       *     })
       */
      const baseMergedPropertyMap = queries.reduce((resultMap, queryKey) => {
        // Merge CSS properties across media queries and variant states

        // Get properties for current media query
        const queryProperties = unmergedCssPropertyMap.get(
          queryKey,
          OrderedMap()
        );

        // Get variant properties if we have a variant state
        const variantProperties = variantState
          ? unmergedCssPropertyMap
              .filter((__, key) => {
                const parsedKey = parseVariantKey(key);
                return (
                  parsedKey.mediaQuery === queryKey &&
                  parsedKey.variantState === variantState
                );
              })
              .reduce((acc, map) => acc.merge(map), OrderedMap())
          : OrderedMap();

        // Merge query and variant properties into result
        return resultMap.merge(queryProperties).merge(variantProperties);
      }, OrderedMap());

      // If we're not dealing with a pseudoState variant, we do not need to merge
      // any variants that affect our pseudo state here:
      if (!pseudoState) {
        // @ts-expect-error - TS18048 - 'result' is possibly 'undefined'. | TS2345 - Argument of type 'BreakpointID | undefined' is not assignable to parameter of type 'BreakpointID'.
        return result.set(variantKey, baseMergedPropertyMap);
      }

      /**
       * The merged property map for the current variantKey **with** merged
       * pseudoStates.
       *
       * E.g. if our style block looks like this:
       *
       *     {
       *       main: 'color: red;',
       *       main_hover: 'font-weight: bold;',
       *       main_focus: 'this-will-not-show-up-because-we-are-not-focused: nope;',
       *       medium: 'background: blue;'
       *       medium_hover: 'font-size: 24px;'
       *     }
       *
       * ...and our `variantKey` is `medium_hover`, then `finalMergedPropertyMap` looks
       * like this:
       *
       *     Map({
       *       // From `main`:
       *       color: true,
       *
       *       // From `main_hover`:
       *       'font-weight': true,
       *
       *       // From `medium`:
       *       background: true,
       *
       *       // From `medium_hover`:
       *       'font-size': true,
       *     })
       */
      const finalMergedPropertyMap = unmergedCssPropertyMap
        .filter((__, otherVariantKey) => {
          // This picks higher MQs that share our pseudoState.
          //
          // E.g. if our variant is `medium_hover`, this will select for
          // `main_hover`, but not `main_focus`:
          return parseVariantKey(otherVariantKey).pseudoState === pseudoState;
        })
        .reduce(
          // @ts-expect-error - TS18048 - 'propertyMap' is possibly 'undefined'. | TS2769 - No overload matches this call.
          (resultMap, propertyMap) => propertyMap.merge(resultMap),
          baseMergedPropertyMap
        );

      // @ts-expect-error - TS18048 - 'result' is possibly 'undefined'. | TS2345 - Argument of type 'BreakpointID | undefined' is not assignable to parameter of type 'BreakpointID'.
      return result.set(variantKey, finalMergedPropertyMap);
    },
    OrderedMap({main: mainPropertyMap}) as OrderedMap<
      BreakpointID,
      OrderedMap<string, boolean | string>
    >
  );
}

const buildMap = weakMemo(
  (
    // @ts-expect-error - TS7006 - Parameter 'styleBlock' implicitly has an 'any' type.
    styleBlock
  ): [
    string,
    OrderedMap<BreakpointID, OrderedMap<string, boolean | string>>,
  ] => [styleBlock._id, getJustCssProperties(styleBlock)]
);
/**
 * Creates a map that can be used to look up if a style block has a specific CSS property set.
 * @param  {Immutable.List<StyleBlockRecord>} styleBlocks          The pure JS list of StyleBlockRecord items in original server order.
 * @return {Immutable.Map<String, Immutable.Map<String, Boolean>}  A guid -> (CSSProperty -> Boolean) Immutable.Map.
 */
export function buildCssPropertyMap(
  styleBlocks: List<StyleBlock>
): CssPropertyMap {
  return Map(styleBlocks.map(buildMap));
}
/**
 * Creates an Immutable.List to keep track of the order of root StyleBlocks.
 * @param  {List}       styleBlocks     The list of StyleBlocks in server order.
 * @param  {Map}        parentIndex   The Map generated by buildParentIndex that tells us if a block is nested.
 * @return {OrderedSet}               The global ordered list of block guids.
 */
export function buildOrderList(
  styleBlocks: List<StyleBlock>,
  parentIndex: Map<string, string>
) {
  const globalGuids: Array<string> = [];

  styleBlocks.forEach((block) => {
    if (parentIndex.has(block.get('_id')) === false) {
      globalGuids.push(block.get('_id'));
    }
  });

  return OrderedSet<string>(fromJS(globalGuids));
}

// @ts-expect-error - TS7006 - Parameter 'parentIndex' implicitly has an 'any' type. | TS7024 - Function implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
const getDepth = weakMemo((parentIndex) => (blockId: any) => {
  const parentId = parentIndex.get(blockId);
  return parentId == null ? 0 : 1 + getDepth(parentIndex)(parentId);
});

/**
 * Adds derived selector data to a block based on its position in the style tree.
 * @param  {StyleBlock}   styleBlock             An immutable StyleBlock record.
 * @param  {StyleBlock[]} options.treePath       The path to this block from a root global block.
 * @param  {Boolean}      options.skipVariants   If true, variants will not be decorated. This is useful for AffectedNodesUtils since it only needs main selector info.
 * @param  {Boolean}      options.hashContentSelectorsForPublishingCss  If true, element-style selectors are content-hashed instead of ID-based. Defaults to false so Designer-side callers (style panel UI, style rule generation) stay aligned with `buildClassNameMap(false)`.
 * @return {StyleBlock}                          The same StyleBlock, but with derived selector data (tokens, variants, score, etc) added.
 */
export function decorateStyleBlock(
  styleBlock: StyleBlock,
  {
    treePath = [styleBlock],
    skipVariants = false,
    slugMap,
    variantDataMap,
    hashContentSelectorsForPublishingCss = false,
  }: {
    treePath?: StyleBlock[];
    skipVariants?: boolean;
    slugMap: Plugins.PluginsType['slugMap'];
    variantDataMap: VariantDataMap;
    hashContentSelectorsForPublishingCss?: boolean;
  }
) {
  const mainSelectorChunks = getMainSelectorChunks(
    treePath,
    hashContentSelectorsForPublishingCss
  );

  const mainSelectorText = getSelectorText(
    mainSelectorChunks,
    slugMap,
    variantDataMap
  );
  if (mainSelectorText === null) return styleBlock;

  const mainSelector: Selector = fromJS({
    text: mainSelectorText,
    score: getSelectorScore(mainSelectorChunks),
    chunks: mainSelectorChunks,
  }) as any;

  // A library tag style's component variant is written at install and only
  // becomes resolvable on first use. `exportStyleBlock` serialises the
  // decorated block, so dropping it breaks the variant on first use.
  const keepUnresolvedVariants = isLibraryTagStyle(styleBlock);

  const decoratedStyleBlock = styleBlock
    .set('selector', mainSelector)
    .update('variants', (variants) => {
      return skipVariants
        ? variants
        : variants.reduce((acc, variant, key) => {
            const parts = parseVariantKey(key);
            const variantChunks = cloneChunks(mainSelectorChunks);
            const lastChunk = variantChunks[variantChunks.length - 1];

            // @ts-expect-error - TS18048 - 'lastChunk' is possibly 'undefined'.
            lastChunk.tokens.push(
              ...getTokensForVariants({
                pseudoState: parts.pseudoState,
                customNodeState: parts.customNodeState,
                variantState: parts.variantState,
              })
            );

            const variantSelectorText = getSelectorText(
              variantChunks,
              slugMap,
              variantDataMap
            );

            // A null selector text means no matching variant data was found in VariantDataMap.
            if (variantSelectorText === null && !keepUnresolvedVariants) {
              return acc;
            }

            const variantSelector = {
              text: variantSelectorText,
              score: getSelectorScore(variantChunks),
              chunks: variantChunks,
            } as const;

            return acc.set(
              key,
              // @ts-expect-error - TS18048 - 'variant' is possibly 'undefined'. | TS2345 - Argument of type '"selector"' is not assignable to parameter of type '"styleLess"'.
              variant.set('selector', fromJS(variantSelector))
            );
          }, OrderedMap());
    });
  return decoratedStyleBlock;
}

export function getMainSelectorChunks(
  treePath: StyleBlock[],
  hashContentSelectorsForPublishingCss: boolean = false
): SelectorChunk[] {
  const chunks: Array<SelectorChunk> = [];
  let mainSelectorChunk = newChunk();

  treePath.forEach((pathBlock) => {
    const {comb, type, name, namespace, origin} = pathBlock;

    // If this next block is a nested selector, we need to start a new chunk
    if (comb === COMBINATORS.DESCENDANT) {
      chunks.push(mainSelectorChunk);
      mainSelectorChunk = newChunk();
    }

    mainSelectorChunk.combinator = comb;

    let className;
    if (type === 'class') {
      className = name;
    } else if (type === 'element') {
      className = getElementStyleClassName(
        pathBlock,
        hashContentSelectorsForPublishingCss
      );
    }

    mainSelectorChunk.tokens.push(
      buildSelectorToken({
        tagName: type === 'tag' ? name : undefined,
        className,
        namespace,
        origin: origin ? origin : undefined,
        isElementStyle: type === 'element',
      })
    );
  });

  chunks.push(mainSelectorChunk);
  return chunks;
}

export function getVariantSelectorChunks(
  variantKey: string,
  mainSelectorChunks: SelectorChunk[]
): SelectorChunk[] {
  const parts = parseVariantKey(variantKey);
  const variantChunks = cloneChunks(mainSelectorChunks);
  const lastChunk = variantChunks[variantChunks.length - 1];

  // @ts-expect-error - TS18048 - 'lastChunk' is possibly 'undefined'.
  lastChunk.tokens.push(
    ...getTokensForVariants({
      pseudoState: parts.pseudoState,
      customNodeState: parts.customNodeState,
      variantState: parts.variantState,
    })
  );

  return variantChunks;
}

function newChunk(): SelectorChunk {
  return {
    tokens: [],
    combinator: null,
  };
}

function cloneChunks(chunks: Array<SelectorChunk>) {
  return chunks.map((chunk) => {
    return {
      combinator: chunk.combinator,
      tokens: chunk.tokens.slice(),
    };
  });
}

type getBlockStyleBlockData = {
  styleBlocks: StyleBlockStateShape['styleBlocks'];
  parentIndex: StyleBlockStateShape['parentIndex'];
};

/**
 * Get a style block from the style block list and optionally decorate it.
 */
export function getBlock(
  styleBlockData: getBlockStyleBlockData,
  guid?: string | null,
  params: DecoratableWithMaps & {
    skipVariants?: boolean;
    hashContentSelectorsForPublishingCss?: boolean;
  } = {}
): StyleBlock | undefined {
  if (!guid) {
    return undefined;
  }

  // @ts-expect-error - TS2345 - Argument of type 'boolean' is not assignable to parameter of type 'StyleBlock'.
  const styleBlock = styleBlockData.styleBlocks.get(guid, false);

  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'StyleBlock' and 'boolean' have no overlap.
  if (styleBlock === false) {
    return undefined;
  }

  if (params.decorate) {
    return decorateStyleBlock(styleBlock, {
      treePath: getTreePath(
        styleBlock,
        styleBlockData.parentIndex,
        styleBlockData.styleBlocks
      ),
      skipVariants: params.skipVariants ?? false,
      slugMap: params.slugMap,
      variantDataMap: params.variantDataMap,
      hashContentSelectorsForPublishingCss:
        params.hashContentSelectorsForPublishingCss ?? false,
    });
  }

  return styleBlock;
}

/**
 * flattenBlock: Flatten a StyleBlockRecord into an ordered array of StyleBlockRecords.
 *
 * This function is manually curried with specialized memoizers for performance.
 *
 * Usage:
 *
 * flattenBlock(params: FlattenBlockParams)(styleBlockState: StyleBlockStateType)(block: StyleBlockRecord)
 *
 * Returns: Array<StyleBlockRecord>
 */
export const flattenBlock = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((variantDataMap: VariantDataMap) =>
      weakMemo(
        (params: {
          rootGuid?: string;
          decorate?: boolean;
          includeFake?: boolean;
          hashContentSelectorsForPublishingCss?: boolean;
        }) =>
          weakMemo((styleBlockData: getBlockStyleBlockData) =>
            weakMemo((block: StyleBlock) => {
              const {
                decorate = false,
                includeFake = false,
                hashContentSelectorsForPublishingCss = false,
              } = params;
              if (!includeFake && block.get('fake')) {
                return [];
              }
              const flattenedListOfBlocks = [block];

              const children = block.get('children');

              children.forEach((childGuid) => {
                const childBlock = getBlock(styleBlockData, childGuid, {
                  decorate,
                  slugMap,
                  variantDataMap,
                  hashContentSelectorsForPublishingCss,
                });
                if (childBlock) {
                  Array.prototype.push.apply(
                    flattenedListOfBlocks,
                    flattenBlock(slugMap)(variantDataMap)(params)(
                      styleBlockData
                    )(childBlock)
                  );
                }
              });

              return flattenedListOfBlocks;
            })
          )
      )
    )
);

const selectorTextPath = ['selector', 'text'];

// We don't inline these default parameters because _getOrderedList uses `weakMemo`,
// and an inline object would appear as a different reference and therefore miss the `weakMemo` cache.
// The two shapes correspond to the two stable values of `hashContentSelectorsForPublishingCss`, so each one anchors its
// own warm cache lineage instead of forcing them to fight over a single key.
const DEFAULT_GETORDEREDLIST_PARAMS = {
  decorate: true,
} as const;
const PUBLISHING_GETORDEREDLIST_PARAMS = {
  decorate: true,
  hashContentSelectorsForPublishingCss: true,
} as const;

const _getOrderedList = weakMemo((slugMap: Plugins.PluginsType['slugMap']) =>
  weakMemo((variantDataMap: VariantDataMap) =>
    weakMemo(
      (params: {
        decorate?: boolean;
        rootGuid?: string;
        includeFake?: boolean;
        hashContentSelectorsForPublishingCss?: boolean;
      }) =>
        weakMemo((data: getOrderedListStyleBlockData) => {
          const {
            rootGuid = null,
            decorate = false,
            hashContentSelectorsForPublishingCss = false,
          } = params;

          const list: Array<StyleBlock> = [];
          const rootGuids = rootGuid
            ? OrderedSet([rootGuid])
            : data.globalOrder;

          rootGuids.forEach((guid) => {
            const block = getBlock(data, guid, {
              decorate,
              slugMap,
              variantDataMap,
              hashContentSelectorsForPublishingCss,
            });
            if (block) {
              Array.prototype.push.apply(
                list,
                flattenBlock(slugMap)(variantDataMap)(params)(data)(block)
              );
            }
          });
          return list;
        })
    )
  )
);

type getOrderedListStyleBlockData = getBlockStyleBlockData & {
  globalOrder: StyleBlockStateShape['globalOrder'];
};

/**
 * Returns an ordered list of style blocks.
 *
 * `hashContentSelectorsForPublishingCss` selects which selector form `decorate` produces for `element`-type blocks
 * (see {@link decorateStyleBlock}).
 */
export function getOrderedList(
  styleBlockData: getOrderedListStyleBlockData,
  {
    slugMap,
    variantDataMap,
    hashContentSelectorsForPublishingCss,
    ...params
  }: DecoratableWithMaps & {
    rootGuid?: string;
    includeFake?: boolean;
    hashContentSelectorsForPublishingCss?: boolean;
  }
): Array<StyleBlock> {
  if (Object.keys(params).length === 0) {
    return _getOrderedList(slugMap ?? emptyObject)(
      variantDataMap ?? emptyVariantDataMap
    )(
      hashContentSelectorsForPublishingCss
        ? PUBLISHING_GETORDEREDLIST_PARAMS
        : DEFAULT_GETORDEREDLIST_PARAMS
    )(styleBlockData);
  }
  return _getOrderedList(slugMap ?? emptyObject)(
    variantDataMap ?? emptyVariantDataMap
  )({...params, hashContentSelectorsForPublishingCss})(styleBlockData);
}

/**
 * Formats a StyleBlockRecord into a server-side (MongoDB-compatible) POJO.
 * @param  {StyleBlockRecord} styleBlock              An Immutable StyleBlock from the StyelBlockStore, which must be decorated with decorateStyleBlock().
 * @param  {Immutable.Map}    options.affectsMap      The current StyleBlockState.affectsMap which stores the _id -> { pageId: count } map.
 * @param  {Map}              options.changedRule     The current StyleBlockState.changedRule data that stores old values for the last ephemerally-changed StyleRule.
 * @return {Object}                                   A POJO that CssSync understands.
 */
export function exportStyleBlock(
  styleBlock: StyleBlock,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ affectsMap: Map<string, Record<any, any>>; changedRule: ChangedRuleType; }': affectsMap, changedRule
  {
    affectsMap,
    changedRule,
  }: {
    affectsMap: Map<string, Record<any, any>>;
    changedRule: ChangedRuleType;
  } = {}
) {
  if (!affectsMap || !changedRule) {
    throw new Error('exportStyleBlock is missing required params!');
  }

  const _id = styleBlock._id;
  const mainRuleGuid = createStyleRuleGuid(_id, 'main');

  const affectsRaw = affectsMap.has(_id) ? affectsMap.get(_id).toJS() : null;

  let affectsClean;

  if (affectsRaw != null) {
    affectsClean = Object.keys(affectsRaw)
      .sort()
      .reduce<Record<string, any>>((result, k) => {
        const count = affectsRaw[k];
        if (count) {
          result[k] = count;
        }
        return result;
      }, {});
  } else {
    affectsClean = emptyObject;
  }

  const mainSelector = styleBlock.getIn(selectorTextPath);
  const mainStyleLess = _exportStyleLess(styleBlock, mainRuleGuid, changedRule);

  const jsonBlock: ServerStyleBlock = {
    _id,
    data: {
      type: styleBlock.type,
      name: styleBlock.name,
      createdBy: styleBlock.createdBy,
      // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string | undefined'.
      sel: mainSelector,
      comb: styleBlock.comb,
      styleLess: mainStyleLess,
      children: styleBlock.children.toArray(),
      pluginType: styleBlock.pluginType,
      variants: styleBlock.variants.reduce<Record<string, any>>(
        (result, variant, variantKey) => {
          const variantRuleGuid = createStyleRuleGuid(_id, variantKey);
          const variantSelector = variant.getIn(selectorTextPath);
          const variantStyleLess = _exportStyleLess(
            variant,
            variantRuleGuid,
            changedRule
          );

          result[variantKey] = {
            sel: variantSelector,
            styleLess: variantStyleLess,
          };

          return result;
        },
        {}
      ),
      affects: affectsClean,
    },
  };
  // Only persist namespace to the server if it exists
  if (styleBlock.namespace && styleBlock.namespace.length) {
    jsonBlock.data.namespace = styleBlock.namespace;
  }

  if (styleBlock.origin) {
    jsonBlock.data.origin = styleBlock.origin;
  }

  return jsonBlock;
}

type ExportStyleBlocksData = {
  styleBlocks: StyleBlockStateShape['styleBlocks'];
  parentIndex: StyleBlockStateShape['parentIndex'];
  affectsMap: StyleBlockStateShape['affectsMap'];
  changedRule: StyleBlockStateShape['changedRule'];
  styleBlockIds: StyleBlockStateShape['styleBlockIds'];
  globalOrder: StyleBlockStateShape['globalOrder'];
};

/**
 * Formats all StyleBlockRecords into a server-side (MongoDB-compatible) POJO.
 */
export function exportStyleBlocks({
  styleBlockData,
  currentPageId,
  slugMap,
  variantDataMap,
  exportAllPageStyles,
  affectedNodesMap,
  hashContentSelectorsForPublishingCss,
}: {
  styleBlockData: ExportStyleBlocksData;
  currentPageId: string;
  slugMap: Plugins.PluginsType['slugMap'];
  variantDataMap: VariantDataMap;
  affectedNodesMap: AffectedNodesByStyleBlockId;
  /**
   * If true, this will export all style blocks associated with the page, including those that were not created on this branch.
   */
  exportAllPageStyles?: boolean;
  /**
   * If true, element-style selectors (the `data.sel` field) are content-hashed
   * (e.g. `._jf38jh`) instead of ID-based.
   */
  hashContentSelectorsForPublishingCss?: boolean;
}): Array<ServerStyleBlock> {
  const allBlocks = getOrderedList(styleBlockData, {
    slugMap,
    variantDataMap,
    hashContentSelectorsForPublishingCss,
  }).map((styleBlock) => {
    const blocksAffected = affectedNodesMap[styleBlock._id];
    return exportStyleBlock(styleBlock, {
      affectsMap: styleBlockData.affectsMap.setIn(
        [styleBlock._id, currentPageId],
        blocksAffected ? blocksAffected.length : 0
      ),
      changedRule: styleBlockData.changedRule,
    });
  });

  if (exportAllPageStyles) {
    return allBlocks;
  }

  // Filter out blocks that were not created on this branch
  // For the main site, this should not filter out any blocks
  const filteredBlocks = allBlocks.filter((styleBlock) =>
    styleBlockData.styleBlockIds.has(styleBlock._id)
  );

  return filteredBlocks;
}

function _exportStyleLess(
  blockOrVariant: StyleBlock | StyleBlockVariant,
  styleRuleGuid: string,
  changedRule: ChangedRuleType
) {
  const oldValues = _getOldValues(styleRuleGuid, changedRule);
  let styleLess = blockOrVariant.get('styleLess') || '';

  if (oldValues) {
    styleLess = updateStyleLessForExport(styleLess, oldValues);
  }

  return styleLess;
}

function updateStyleLessForExport(
  cssText: string,
  oldValues: {
    [key: string]: unknown;
  }
): string {
  const list: Array<any | string> = [];

  if (!cssText || cssText.trim().length === 0) {
    return '';
  }

  const lines = cssText.split(';');

  for (const line of lines) {
    const propertyValue = cssTextToPropertyValue(line);
    if (propertyValue == null) continue;

    const {property, value} = propertyValue;
    const overrideValue = oldValues[property];

    // If this value is set in the current state, but is null in oldValues, it means it was sent ephemerally, so ignore it
    if (overrideValue === null) continue;

    // If there is a pre-ephemeral non-null value, override the current value
    if (overrideValue !== undefined) {
      list.push(`${property}: ${String(overrideValue)};`);

      // Otherwise just leave the current value alone
    } else {
      list.push(`${property}: ${String(value)};`);
    }
  }

  return list.join(' ');
}

function _getOldValues(styleRuleGuid: string, changedRule: ChangedRuleType) {
  if (
    changedRule.ephemeral &&
    changedRule.guid === styleRuleGuid &&
    changedRule.oldValues.size
  ) {
    return changedRule.oldValues.toJS();
  }

  return null;
}

/**
 * Returns the class name for a given style block
 */
export const toCssClass = (
  styleBlock: StyleBlock,
  slugMap: Plugins.PluginsType['slugMap'],
  hashContentSelectorsForPublishingCss: boolean = false
) => {
  if (styleBlock.type === 'element') {
    return getElementStyleClassName(
      styleBlock,
      hashContentSelectorsForPublishingCss
    );
  }

  const name = styleBlock.get('name') ?? styleBlock.get('_id');

  const namespace = styleBlock.get('namespace', '');
  const origin = styleBlock.get('origin');

  // Tag-based style blocks installed from libraries should not prefix the
  // style block's name as this value is used when creating CSS tag selectors
  const userlandPrefix =
    styleBlock.type === 'tag'
      ? null
      : origin
        ? getUserlandPrefix(origin, slugMap)
        : null;

  return toClassName(name, {userlandPrefix, namespace});
};

export const getStylesFromStyleBlock = ({
  styleBlock,
  mediaQuery,
  customNodeState,
  pseudoState,
  variantState,
}: {
  styleBlock: StyleBlock;
  mediaQuery?: BreakpointID;
  pseudoState?: PseudoStateKey | null | undefined;
  customNodeState?: CustomNodeStateKey | null | undefined;
  variantState?: VariantStateKey | null | undefined;
}) => {
  const variantKey = createVariantKey({
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
  });
  // @ts-expect-error - TS2345 - Argument of type '{ variantKey: string; decorate: boolean; }' is not assignable to parameter of type '{ variantKey: string; }'.
  return getStyleLess(styleBlock, {variantKey, decorate: false});
};

/**
 * Reads a styleLess value from a main block or one of its variants.
 * @param  {StyleBlockRecord} styleBlock          The StyleBlock.
 * @param  {String}           options.variantKey  The variant key, e.g. 'medium_hover'
 * @return {String}                               The styleLess of the variant if it exists, or null if the block or variant is missing.
 */
export function getStyleLess(
  styleBlock: StyleBlock,
  // @ts-expect-error - TS2741 - Property 'variantKey' is missing in type '{}' but required in type '{ variantKey: string; }'.
  {
    variantKey,
  }: {
    variantKey: string;
  } = {}
): string | null {
  if (!styleBlock) {
    return null;
  }
  const variantPath =
    !variantKey || variantKey === 'main' ? [] : ['variants', variantKey];
  const lessPath = [...variantPath, 'styleLess'];

  const less = styleBlock.getIn(lessPath);
  return typeof less === 'string' ? less : null;
}

export const updateCssPropertyMap = ({
  state,
  renames,
  droppedFonts,
  nextId,
  idMap,
  parentIdMap,
}: any) => ({
  state: state.set(
    'cssPropertyMap',
    buildCssPropertyMap(state.styleBlocks.toList())
  ),
  renames,
  droppedFonts,
  nextId,
  idMap,
  parentIdMap,
});

const getBlockDepth = (parentIndex: Map<any, never>) => (block: any) =>
  getDepth(parentIndex)(block._id);

export const sortByDepth = (blocks: any) =>
  // @ts-expect-error - TS2345 - Argument of type 'Map<string, any>' is not assignable to parameter of type 'Map<any, never>'.
  blocks.sortBy(getBlockDepth(buildParentIndex(blocks)));

const prepareStyleBlockForSelectorComparing = (
  styleBlock: StyleBlock,
  skipOriginCheck: boolean = false
): StyleBlock => {
  return styleBlock
    .set('_id', '')
    .update('name', getPrefix)
    .set('children', OrderedSet())
    .set('createdBy', '')
    .set('variants', '')
    .set('styleLess', '')
    .set('origin', skipOriginCheck ? null : styleBlock.origin);
};

const styleBlocksSelectorsMatch =
  (blockA: StyleBlock, skipOriginCheck: boolean = false) =>
  (blockB: StyleBlock) =>
    prepareStyleBlockForSelectorComparing(blockA, skipOriginCheck).equals(
      prepareStyleBlockForSelectorComparing(blockB, skipOriginCheck)
    );

const styleBlockParentMatches =
  (parentId: any) =>
  (parentIndex: Map<string, string>) =>
  (styleBlocks: Map<string, StyleBlock>) =>
  (skipOriginCheck: boolean = false) =>
  (styleBlock: StyleBlock) => {
    const otherParentId = immLookup(styleBlock._id)(parentIndex);

    // If IDs match exactly, they're the same
    if (
      parentId === otherParentId ||
      extractBool(parentId.map(equals).ap(otherParentId))
    ) {
      return true;
    }

    // If skipOriginCheck is true, compare parent blocks by their selector properties
    if (skipOriginCheck && parentId && otherParentId) {
      const parentIdValue = maybe<string | null>(null)((id: string) => id)(
        parentId
      );
      const otherParentIdValue = maybe<string | null>(null)((id: string) => id)(
        otherParentId
      );

      if (parentIdValue && otherParentIdValue) {
        const parentBlock = styleBlocks.get(parentIdValue);
        const otherParentBlock = styleBlocks.get(otherParentIdValue);

        if (parentBlock && otherParentBlock) {
          return prepareStyleBlockForSelectorComparing(
            parentBlock,
            skipOriginCheck
          ).equals(
            prepareStyleBlockForSelectorComparing(
              otherParentBlock,
              skipOriginCheck
            )
          );
        }
      }
    }

    return false;
  };

const isRootClass = (styleBlock: StyleBlock) =>
  styleBlock.type === 'class' && styleBlock.comb === COMBINATORS.NONE;

export function isComboClass(
  styleBlock: StyleBlock | RawStyleBlockServerDataType
) {
  return Boolean(
    styleBlock.type === 'class' && styleBlock.comb === COMBINATORS.COMBO
  );
}

/**
 * Creates a new StyleBlock in the style tree.
 * @param  {StyleBlockRecord} options.styleBlock  An Immutable StyleBlockRecord previously constructed by StyleBlockUtils.buildStyleBlock()
 * @param  {String}           options.parentGuid  If this block is nested, the GUID of the parent block. If this is missing, implies the block is global.
 */
export function createStyleBlock(
  state: StyleBlockStateType,
  {
    styleBlock,
    parentGuid,
  }: {
    styleBlock: StyleBlock;
    parentGuid?: string | null;
  }
): StyleBlockStateType {
  const _id = styleBlock.get('_id');

  // If the StyleBlock already exists in the tree, then do nothing
  if (state.getBlock(_id)) {
    return state;
  }

  return state
    .resetChangedRule()
    .resetAddedRule()
    .set('_hack_addedRuleGuid', _id)
    .update('styleBlocks', (styleBlocks) => {
      return styleBlocks
        .set(_id, styleBlock)
        .updateIn([parentGuid, 'children'], (children) => {
          return children ? children.add(_id) : children;
        });
    })
    .update('parentIndex', (parentIndex) => {
      return parentGuid ? parentIndex.set(_id, parentGuid) : parentIndex;
    })
    .update('globalOrder', (globalOrder) => {
      return parentGuid ? globalOrder : globalOrder.add(_id);
    })
    .update('affectsMap', (affectsMap) => {
      // Need to add a blank map here so that the affects map picks up the new node on the next update
      if (!affectsMap.has(_id)) {
        affectsMap = affectsMap.set(_id, Map());
      }

      return affectsMap;
    })
    .update('cssPropertyMap', (cssPropertyMap) => {
      if (!cssPropertyMap.has(_id)) {
        cssPropertyMap = cssPropertyMap.set(_id, OrderedMap());
      }
      return cssPropertyMap;
    })
    .update('styleBlockIds', (styleBlockIds) => {
      styleBlockIds = styleBlockIds.add(_id);
      if (parentGuid) {
        styleBlockIds = styleBlockIds.add(parentGuid);
      }
      return styleBlockIds;
    });
}

const updateStateBreakpoints = (
  state: StyleBlockStateType,
  styleBlock: StyleBlock
) => {
  if (
    Object.keys(state.breakpoints).length === LARGER_BREAKPOINTS.length ||
    !styleBlock.variants.size
  ) {
    return state;
  }

  const newBreakpointIds = styleBlock.variants.reduce<Array<any>>(
    (acc, _value, key) => {
      const {mediaQuery} = parseVariantKey(key);
      acc.push(mediaQuery);
      return acc;
    },
    []
  );

  if (!newBreakpointIds.length) return state;

  const breakpoints = LARGER_BREAKPOINTS.filter(
    ({id}) => state.breakpoints[id] || newBreakpointIds.includes(id)
  );

  return state.set('breakpoints', keyByBreakpoints(breakpoints));
};

const avoidClassNameConflict = createConflictResolver((s) =>
  toClassName(s, {
    // NOTE: We don't need a `userlandPrefix` here, since we filter out blocks
    // that don't have a matching `origin`, and thus can treat these classes as
    // being part of the same style sheet without any prefix:
    userlandPrefix: null,
  })
);
const getName = (styleBlock: StyleBlock): string => styleBlock.name;

const handleRenamedRootClasses = (renames: StyleBlocksRenames) => {
  const renamesMap = renames.reduce<Record<string, string>>((acc, current) => {
    acc[current.from] = current.to;
    return acc;
  }, {});
  return (name: string): string => {
    const renamed = renamesMap[name];
    return renamed || name;
  };
};

const originsMatch = (
  originA: StyleBlock['origin'],
  originB: StyleBlock['origin']
): boolean =>
  originA != null && originB != null
    ? namesEqual(originA, originB)
    : originA === originB;

const buildComboClassNames = (
  state: StyleBlockStateType,
  origin: StyleBlock['origin']
): Map<string, string> =>
  state.styleBlocks.reduce<Map<string, string>>(
    (acc, sb, key) =>
      isComboClass(sb) && originsMatch(sb.origin, origin)
        ? acc.set(key, getName(sb))
        : acc,
    Map()
  );

const createNewBlockWithIDAndStyleBlock = (
  id: string,
  styleBlock: StyleBlock,
  origin: StyleBlock['origin'],
  state: StyleBlockStateType,
  renames: StyleBlocksRenames,
  enforceUniqueComboClasses: boolean = false,
  comboClassNames: Map<string, string> | undefined = undefined
): StyleBlock => {
  return styleBlock
    .set('_id', id)
    .set('origin', origin)
    .update('name', (name) => {
      if (isRootClass(styleBlock)) {
        const otherNames = state.styleBlocks
          .filter((sb) => isRootClass(sb) && originsMatch(sb.origin, origin))
          .map(getName);

        return avoidClassNameConflict(otherNames)(name);
      }

      // If the root class was renamed we need to rename
      // the combo class to match the new name.
      // Tag names are rendered into selectors verbatim rather than slugified,
      // so a conflict-suffixed name yields invalid CSS (`.rich-text-2 h1 2`).
      if (styleBlock.type !== 'tag') {
        name = handleRenamedRootClasses(renames)(name);
      }

      // By default, combo classes can share names since they're typically
      // contextually distinct. When enforceUniqueComboClasses is true, we
      // ensure uniqueness to prevent unintended style collisions. eg. when
      // unlinking component instances with style variants.
      if (enforceUniqueComboClasses && isComboClass(styleBlock)) {
        const resolvedComboClassNames =
          comboClassNames ?? buildComboClassNames(state, origin);

        return resolvedComboClassNames
          ? avoidClassNameConflict(resolvedComboClassNames)(name)
          : name;
      }

      return name;
    });
};

type UpdatedStyleBlockState = [StyleBlock, StyleBlockStateType];
type IDMap = {
  [key: string]: string;
};

const getStyleBlockWithMatchingSelectors = (
  styleBlock: StyleBlock,
  state: StyleBlockStateType,
  parentIdMap: IDMap,
  skipOriginCheck: boolean = false
): Option<StyleBlock> =>
  find(
    allPass([
      styleBlocksSelectorsMatch(styleBlock, skipOriginCheck),
      styleBlockParentMatches(lookup(styleBlock._id)(parentIdMap))(
        state.parentIndex
      )(state.styleBlocks)(skipOriginCheck),
    ])
  )(state.styleBlocks);

const createNewBlockAndUpdateState = (
  id: string,
  styleBlock: StyleBlock,
  state: StyleBlockStateType,
  parentIdMap: IDMap,
  origin: StyleBlock['origin'],
  renames: StyleBlocksRenames,
  enforceUniqueComboClasses: boolean = false,
  comboClassNames: Map<string, string> | undefined = undefined
): UpdatedStyleBlockState => {
  const newStyleBlock = createNewBlockWithIDAndStyleBlock(
    id,
    styleBlock,
    origin,
    state,
    renames,
    enforceUniqueComboClasses,
    comboClassNames
  );

  return [
    newStyleBlock,
    updateStateBreakpoints(
      createStyleBlock(state, {
        // Reset children since their IDs will change. The new IDs will be
        // added by `createStyleBlock` based on `parentGuid`.
        styleBlock: newStyleBlock.set('children', OrderedSet()),
        parentGuid: parentIdMap[styleBlock._id],
      }),
      newStyleBlock
    ),
  ];
};

type StyleBlockOriginMap = QualifiedMap<StyleBlockId, StyleBlock>;

type StyleBlocksRenames = {
  from: string;
  to: string;
}[];

type StyleBlocksAccumulator = {
  importStrategy: ImportStrategyType;
  state: StyleBlockStateType;
  renames: StyleBlocksRenames;
  droppedFonts: string[];
  nextId: string;
  idMap: IDMap;
  parentIdMap: IDMap;
  slugMap: Plugins.PluginsType['slugMap'];
  isOptimizeLibrariesPerformanceEnabled?: boolean;
  enforceUniqueComboClasses?: boolean;
  /**
   * If provided, used to produce the `origin` field on a style block whose ID
   * was replaced.
   *
   * For now used only by Workspace Libraries, whose plugins have a Plugin
   * Import Strategy (not to be confused with `ImportStrategyType` used in this
   * file) of `VALUE`, i.e. copy by value.
   */
  originPlugin?: PluginName;
  /**
   * If provided, used to prevent duplicate style blocks with the same `origin`.
   *
   * For now used only by Workspace Libraries, whose plugins have a Plugin
   * Import Strategy (not to be confused with `ImportStrategyType` used in this
   * file) of `VALUE`, i.e. copy by value.
   */
  originMap?: StyleBlockOriginMap;
  /**
   * If true, skips origin checking during style block accumulation.
   */
  skipOriginCheck?: boolean;
  /**
   * Cached combo class names, built lazily on the first iteration of
   * `accumulateStyleBlocks` and maintained incrementally thereafter.
   */
  comboClassNames?: Map<string, string>;
};

/**
 * Builds a QualifiedMap {[origin] => boolean}
 * used to lookup if a style block with a given `origin` already exists.
 */
const buildStyleBlockOriginMap = weakMemo(
  (styleBlockState: StyleBlockStateType): StyleBlockOriginMap => {
    const originMap_mut =
      new MutableQualifiedMap() satisfies StyleBlockOriginMap;

    styleBlockState.styleBlocks.forEach((styleBlock) => {
      if (styleBlock.origin) {
        originMap_mut.set_mut(styleBlock.origin, styleBlock);
      }
    });
    // This method is memoized so we must return an immutable map
    return originMap_mut.asNonMutableCopy();
  }
);

/**
 * Executed for each style block during an import phase. Checks if the style block
 * already exists in the state and determines what to do depending on the importStrategy.
 */
export const accumulateStyleBlocks = (
  {
    importStrategy,
    state,
    renames,
    droppedFonts,
    nextId,
    idMap,
    parentIdMap,
    originPlugin,
    originMap: originMap_,
    slugMap,
    isOptimizeLibrariesPerformanceEnabled,
    enforceUniqueComboClasses = false,
    skipOriginCheck = false,
    comboClassNames: prevComboClassNames,
  }: StyleBlocksAccumulator,
  [dropped, styleBlock]: [string[], StyleBlock]
): StyleBlocksAccumulator => {
  const origin =
    originPlugin != null ? qualify(originPlugin, styleBlock._id) : null;

  const originMap =
    originMap_ ?? (origin ? buildStyleBlockOriginMap(state) : undefined);

  // If we are enforcing unique combo classes, build the combo class names only once
  // and use the previous combo class names if they are available
  const comboClassNames =
    prevComboClassNames ??
    (enforceUniqueComboClasses
      ? buildComboClassNames(state, origin)
      : undefined);

  const returnAccumulator = (
    nextStyleBlock: StyleBlock,
    nextState: StyleBlockStateType
  ): StyleBlocksAccumulator => {
    const willIncrementId = nextStyleBlock._id === nextId;

    /**
     * Checks that the style block is:
     * 1. A tag-based selector
     * 2. Not a descendant selector
     * 3. Not from a Workspace Library
     * 4. Will receive a new ID during this import
     *
     * We're monitoring these specific conditions based on observed patterns in
     * corrupted tag blocks, even though we haven't been able to repro the issue
     * directly. This helps us gather data on potential causes of STYL-1104.
     */
    if (
      nextStyleBlock.type === 'tag' &&
      nextStyleBlock.comb === COMBINATORS.NONE &&
      nextStyleBlock.origin === null &&
      willIncrementId
    ) {
      reportBugOnce(
        'TagStyleBlockImportAnomaly',
        'Detected potential tag style block corruption during import - new ID assigned to direct tag selector',
        {
          blockData: nextStyleBlock,
          team: 'styles',
          ticket: 'https://webflow.atlassian.net/browse/STYL-1104',
        }
      );
    }

    return {
      state: nextState,
      renames:
        willIncrementId && styleBlock.name !== nextStyleBlock.name
          ? (renames.concat([
              {from: styleBlock.name, to: nextStyleBlock.name},
            ]) as {
              from: string;
              to: string;
            }[])
          : renames,

      droppedFonts: droppedFonts.concat(dropped) as string[],

      nextId: willIncrementId ? incrementGuid(nextId) : nextId,

      idMap:
        nextStyleBlock._id === styleBlock._id
          ? idMap
          : assoc(styleBlock._id)(nextStyleBlock._id)(idMap),

      parentIdMap:
        styleBlock.children.size === 0
          ? parentIdMap
          : styleBlock.children.reduce(
              (mut_pIdMap, childId) => {
                mut_pIdMap[childId] = nextStyleBlock._id;
                return mut_pIdMap;
              },
              // Mutate in-place to avoid copying on every iteration:
              {...parentIdMap}
            ),

      slugMap,
      isOptimizeLibrariesPerformanceEnabled,
      originPlugin,
      originMap,
      importStrategy,
      enforceUniqueComboClasses,
      skipOriginCheck,
      comboClassNames:
        comboClassNames && isComboClass(nextStyleBlock)
          ? comboClassNames.set(nextStyleBlock._id, nextStyleBlock.name)
          : comboClassNames,
    };
  };

  // If a style block with the same origin already exists, then we
  // don't need to create a new one and we can early return
  // Skip this check if skipOriginCheck is true
  const existingStyleBlock =
    !skipOriginCheck && origin && originMap?.get(origin);

  if (existingStyleBlock) {
    return returnAccumulator(existingStyleBlock, state);
  }

  // Skip the expensive selector matching scan when:
  // - COPY strategy: both match and no-match paths create a new block unconditionally
  // - Library block with origin: already handled by the origin map check above
  const styleBlockWithMatchingSelectors: Option<StyleBlock> =
    importStrategy === 'COPY' ||
    (isOptimizeLibrariesPerformanceEnabled && origin)
      ? None
      : getStyleBlockWithMatchingSelectors(
          styleBlock,
          state,
          parentIdMap,
          skipOriginCheck
        );

  /**
   * This creates a new style block and returns the updated state in
   * the case there is no style blocks with matching selector.
   */
  const createNewBlockWithID = (id: string): UpdatedStyleBlockState => {
    return createNewBlockAndUpdateState(
      id,
      styleBlock,
      state,
      parentIdMap,
      origin,
      renames,
      enforceUniqueComboClasses,
      comboClassNames
    );
  };

  /**
   * If we have a style block with matching selectors, we need to determine
   * how to import it.
   */
  const importWithMatchingSelector = (
    existingBlockWithSelector: StyleBlock
  ) => {
    return (id: string): UpdatedStyleBlockState => {
      switch (importStrategy) {
        /**
         * If the strategy is KEEP we return the existing style block
         * which ultimately means do nothing!
         */
        case 'KEEP': {
          return [existingBlockWithSelector, state];
        }

        /**
         * If the strategy is COPY we always create a new style block
         */
        case 'COPY': {
          return createNewBlockAndUpdateState(
            id,
            styleBlock,
            state,
            parentIdMap,
            origin,
            renames,
            enforceUniqueComboClasses,
            comboClassNames
          );
        }

        /**
         * If the strategy is OVERRIDE we update the style block's
         * styles and then replace it in the state.
         *
         * Since the new styles could affect the breakpoints, we
         * also update the breakpoints.
         */
        case 'OVERRIDE': {
          const blockWithOverriddenStyles = existingBlockWithSelector.set(
            'styleLess',
            styleBlock.styleLess
          );
          return [
            blockWithOverriddenStyles,
            updateStateBreakpoints(
              state.setIn(
                ['styleBlocks', blockWithOverriddenStyles._id],
                blockWithOverriddenStyles
              ),
              blockWithOverriddenStyles
            ),
          ];
        }

        /**
         * If the strategy is DEFAULT or not provided we compare the style blocks further
         * by checking to see if there styles and variants are also equal. If they are
         * we don't need to reimport it because it's already in the state.
         *
         * If the styles don't match, we create a new style block with a new ID
         * and update the state along with the breakpoints.
         */
        default: {
          const stylesMatch =
            styleBlock.styleLess === existingBlockWithSelector.styleLess &&
            styleBlock.variants.equals(existingBlockWithSelector.variants);

          if (!stylesMatch) {
            return createNewBlockAndUpdateState(
              id,
              styleBlock,
              state,
              parentIdMap,
              origin,
              renames
            );
          }

          return [existingBlockWithSelector, state];
        }
      }
    };
  };

  /**
   * If there isn't a style block with matching selectors, create a new one.
   * Otherwise, import the style block with the matching selector.
   */
  const importStyleBlockWithNewID = maybe(createNewBlockWithID)(
    importWithMatchingSelector
  )(styleBlockWithMatchingSelectors);

  const [newStyleBlock, updatedState]: UpdatedStyleBlockState =
    importStyleBlockWithNewID(nextId);

  return returnAccumulator(newStyleBlock, updatedState);
};

/**
 * Constructs the hierarchical tree path for a given style block up to the root block.
 * This function starts from the specified style block and recursively traverses up the tree using the parentIndex map.
 * It collects each block along the path, effectively reconstructing the inheritance or nesting sequence from the root to the specified block.
 *
 * @param {StyleBlock} styleBlock - The StyleBlock for which the tree path is being constructed.
 * @param {Map<string, string>} parentIndex - A map where each key is a block ID that maps to its corresponding parent id.
 * @param {Map<string, StyleBlock>} styleBlockIndex - A map where each key is a block ID that maps to its corresponding StyleBlock.
 * @return {StyleBlock[]} An array of StyleBlocks representing the path from the root to the given style block, ordered from root to leaf.
 */
export function getTreePath(
  styleBlock: StyleBlock,
  parentIndex: Map<string, string>,
  styleBlockIndex: Map<string, StyleBlock>
): StyleBlock[] {
  const treePath = [styleBlock];
  let currentBlock = styleBlock;

  while (parentIndex.has(currentBlock._id)) {
    const parentId = parentIndex.get(currentBlock._id);
    currentBlock = styleBlockIndex.get(parentId);

    if (!currentBlock) {
      break;
    }

    treePath.push(currentBlock);
  }

  return treePath.reverse();
}

/**
 * Creates an ID for a default tag-based style block where the ID is a combination of
 * a `'default-'` prefix and the HTML tag's name.
 *
 * @example```
 *   createDefaultTagStyleBlockId('h1') // Returns 'default-h1'
 * ```
 */
export function createDefaultTagStyleBlockId(tagName: string) {
  return `default-${tagName}`;
}

const getVariantState = (variantKey: string): VariantStateKey | null =>
  parseVariantKey(variantKey).variantState;

const isNonNullVariantStateKey = (
  state: VariantStateKey | null
): state is VariantStateKey => state !== null;

/**
 * Internal util for producing either a plain string representing a class name
 * for simple `StyleBlock`s, or a `{name, variantStateKeys}` object for
 * `StyleBlock`s with `variantState` encodings within their `variants` data.
 */
const getClassNameData = singleMemo(
  (hashContentSelectorsForPublishingCss: boolean) =>
    weakMemo((slugMap: Plugins.PluginsType['slugMap']) =>
      weakMemo((block: StyleBlock): string | ClassNameAndVariantStateKeys => {
        // Create a KeySet of variant state keys from the block's variants
        const variantStateKeys = KeySet.fromArray(
          Array.from(block.variants.keys()) // Get all variant keys
            .map(getVariantState) // Extract the variant state from each key
            .filter(isNonNullVariantStateKey) // Remove any null states
        );

        const name = toCssClass(
          block,
          slugMap,
          hashContentSelectorsForPublishingCss
        );
        // Finally, if we've seen any `variantStateKeys`, produce a
        // `ClassNameAndVariantStateKeys` object; otherwise just return the class
        // name as usual:
        return KeySet.size(variantStateKeys) > 0
          ? {name, variantStateKeys}
          : name;
      })
    )
);

type IterableStyleBlocks = ImmutableIterable<any, StyleBlock>;

const buildClassNameMap_ = singleMemo(
  (hashContentSelectorsForPublishingCss: boolean) =>
    weakMemo((slugMap: Plugins.PluginsType['slugMap']) =>
      weakMemo((styleBlocks: IterableStyleBlocks): ClassNameMap => {
        const result: ClassNameMap = {};
        for (const block of styleBlocks.values()) {
          result[block._id] = getClassNameData(
            hashContentSelectorsForPublishingCss
          )(slugMap)(block);
        }
        return result;
      })
    )
);

/**
 * Creates a map that can be used to look up the class name for a specific style block id.
 *
 * Renders element style class names as the style block ID.
 */
export const buildClassNameMap =
  (slugMap: Plugins.PluginsType['slugMap']) =>
  (styleBlocks: IterableStyleBlocks) =>
    buildClassNameMap_(false)(slugMap)(styleBlocks);

/**
 * Creates a map that can be used to look up the class name for a specific style block id.
 *
 * Renders element style class names as a hash of the style content.
 */
export const buildClassNameMapForPublishing =
  (slugMap: Plugins.PluginsType['slugMap']) =>
  (styleBlocks: IterableStyleBlocks) =>
    buildClassNameMap_(true)(slugMap)(styleBlocks);

/**
 * Duplicates variant style entries across all style blocks from a source variant to a new variant.
 *
 * Iterates over all style blocks and for each one, finds variant entries whose `variantState`
 * matches `sourceVariantStateKey`. Those entries are duplicated under a new variant key
 * using `newVariantStateKey`.
 *
 * This is used when duplicating a single component style variant (as opposed to
 * `getNextStyleBlockState` in `duplicateVariantData.ts` which handles full component duplication).
 *
 * @param styleBlockState  The current style block state
 * @param sourceVariantStateKey  The variant state key to copy from (null for base variant)
 * @param newVariantStateKey  The variant state key to copy to
 * @returns Updated style block state with duplicated variant entries and changedRule.guid set to the new variant rule GUIDs
 */
export function duplicateVariantStyles(
  styleBlockState: StyleBlockStateType,
  sourceVariantStateKey: VariantStateKey | null,
  newVariantStateKey: VariantStateKey
): StyleBlockStateType {
  const guidChanges: string[] = [];

  const updatedStyleBlocks = styleBlockState.styleBlocks.map((block) => {
    const variantsToDuplicate = block.variants.reduce(
      (
        acc: Record<string, StyleBlockVariant>,
        variant: StyleBlockVariant,
        key: string
      ) => {
        const parsedVariantKey = parseVariantKey(key);

        // Skip if variant state key does not match
        if (parsedVariantKey.variantState !== sourceVariantStateKey) return acc;

        // Create new variant key with the target variant state
        const newVariantKey = createVariantKey({
          ...parsedVariantKey,
          variantState: newVariantStateKey,
        });

        // Keep track of the changed rules
        guidChanges.push(createStyleRuleGuid(block._id, newVariantKey));

        acc[newVariantKey] = variant;
        return acc;
      },
      {}
    );

    if (Object.keys(variantsToDuplicate).length) {
      return block.mergeIn(['variants'], variantsToDuplicate);
    }
    return block;
  });

  if (!guidChanges.length) return styleBlockState;

  return styleBlockState
    .set('styleBlocks', updatedStyleBlocks)
    .setIn(['changedRule', 'guid'], guidChanges);
}

/**
 * Utility that returns any style blocks that have one or more `children` IDs
 * that do not exist in the style block state.
 *
 * Part of this investigation: https://webflow.atlassian.net/browse/STYL-2299
 */
export const getBlocksWithMissingChildren = (
  styleBlockState: StyleBlockStateType
): StyleBlock[] | null => {
  try {
    let blocksWithMissingChildren: StyleBlock[] | null = null;
    for (const [, parent] of styleBlockState.styleBlocks) {
      if (
        parent.children.some(
          (childId) => !styleBlockState.styleBlocks.has(childId)
        )
      ) {
        blocksWithMissingChildren ??= [];
        blocksWithMissingChildren.push(parent);
      }
    }

    return blocksWithMissingChildren;
  } catch (error) {
    reportBugOnce(
      'MissingChildStyleBlocks',
      `Error while getting missing child style blocks`,
      {
        team: 'styles',
        ticket: 'https://webflow.atlassian.net/browse/STYL-2299',
        error,
      }
    );

    return null;
  }
};

const forTesting = {createNewBlockWithIDAndStyleBlock};
export {forTesting};
